import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Film, ShoppingBag, Users, ShieldCheck, Gamepad2, LogOut, RefreshCw, MessageCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { BubzLogo } from "./BubzLogo";
import PinDialog from "./PinDialog";
import ScreenTimeGuard from "./ScreenTimeGuard";

const KID_TABS = [
  { to: "/watch", label: "Watch", icon: Film, color: "#FF5A79" },
  { to: "/games", label: "Games", icon: Gamepad2, color: "#9B5DE5" },
];
const PARENT_TABS = [
  { to: "/watch", label: "Watch", icon: Film, color: "#FF5A79" },
  { to: "/marketplace", label: "Market", icon: ShoppingBag, color: "#FFC02D" },
  { to: "/social", label: "Playground", icon: Users, color: "#2AB7CA" },
  { to: "/messages", label: "Chats", icon: MessageCircle, color: "#9B5DE5" },
  { to: "/parent", label: "Parent", icon: ShieldCheck, color: "#2EC4B6" },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeKid, isKid, logout, setActiveKid } = useAuth();
  const [pinOpen, setPinOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const tabs = isKid ? KID_TABS : PARENT_TABS;
  const stickerCount = activeKid?.stickers?.length || 0;

  const loadUnread = useCallback(async () => {
    try { const { data } = await api.get("/threads-unread/count"); setUnread(data.unread || 0); }
    catch (e) { /* not signed in yet */ }
  }, []);

  useEffect(() => {
    if (isKid || !activeKid) return;
    loadUnread();
    const id = setInterval(loadUnread, 15000);
    return () => clearInterval(id);
  }, [isKid, activeKid, loadUnread, location.pathname]);

  const onSwitch = () => {
    if (isKid) { setPinOpen(true); return; }
    navigate("/profiles");
  };
  const doSwitch = () => {
    setPinOpen(false);
    setActiveKid(null);
    navigate("/profiles");
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-amber-100/80 px-4 sm:px-6 py-3 shadow-sm">
      <ScreenTimeGuard />
      <PinDialog open={pinOpen} onClose={() => setPinOpen(false)} onSuccess={doSwitch}
        title="Leaving kid profile" subtitle="Enter the parent PIN to switch profiles." />
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <button onClick={() => navigate(isKid ? "/watch" : "/marketplace")} className="shrink-0" data-testid="nav-logo">
          <BubzLogo size="sm" />
        </button>

        <nav className="flex items-center gap-1 sm:gap-2 bg-slate-100 rounded-full p-1">
          {tabs.map(({ to, label, icon: Icon, color }) => {
            const active = location.pathname === to;
            return (
              <button key={to} data-testid={`nav-${label.toLowerCase()}`} onClick={() => navigate(to)}
                className={`relative flex items-center gap-1.5 rounded-full px-3 sm:px-4 py-2 text-sm font-bold transition-all ${
                  active ? "text-white shadow" : "text-slate-500 hover:text-slate-800"
                }`}
                style={active ? { backgroundColor: color } : {}}>
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{label}</span>
                {to === "/messages" && unread > 0 && (
                  <span data-testid="nav-unread-badge"
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF5A79] text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
                    {unread}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          {isKid && stickerCount > 0 && (
            <span data-testid="sticker-count" className="hidden sm:flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 px-3 py-1 text-sm font-bold">
              ⭐ {stickerCount}
            </span>
          )}
          {activeKid && (
            <button onClick={onSwitch} data-testid="switch-profile-btn" title="Switch profile"
              className="flex items-center gap-2 rounded-full bg-white border-2 border-amber-100 pl-1 pr-3 py-1 hover:border-amber-200 transition">
              <span className="h-7 w-7 rounded-full flex items-center justify-center text-sm" style={{ backgroundColor: (activeKid.avatar_color || "#2EC4B6") + "33" }}>
                {activeKid.avatar_emoji || "🛡️"}
              </span>
              <span className="text-sm font-semibold text-slate-700 hidden sm:inline">{activeKid.name}</span>
              <RefreshCw className="h-3.5 w-3.5 text-slate-400" />
            </button>
          )}
          <button onClick={logout} data-testid="logout-btn" title="Log out"
            className="h-9 w-9 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </header>
  );
}
